import { useState } from "react";
import { Check, Circle } from "lucide-react";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useMoneyFoundation, useSaveMoneyHabits, useMoneyCheckin, useCompleteMoneyLesson,
} from "@/hooks/useMoneyFoundation";
import { MONEY_LESSONS } from "@/lib/desk/foundations";

const btn = "h-9 px-4 rounded-md text-[11px] uppercase tracking-[0.18em] font-semibold";

export function SetupForm({ habits, onDone }: { habits: ReturnType<typeof useMoneyFoundation>["habits"]; onDone: () => void }) {
  const save = useSaveMoneyHabits();
  const [tracked, setTracked] = useState(!!habits?.spending_tracked);
  const [bills, setBills] = useState(!!habits?.bills_listed);
  const [opened, setOpened] = useState(!!habits?.savings_account_opened);
  const [auto, setAuto] = useState(!!habits?.auto_transfer_set);
  const [amount, setAmount] = useState(habits?.auto_transfer_amount?.toString() ?? "");
  const [plan, setPlan] = useState(habits?.when_then_plan ?? "");
  return (
    <>
      <DialogHeader><DialogTitle>Money setup</DialogTitle></DialogHeader>
      <div className="space-y-4 text-sm">
        <label className="flex items-start gap-3">
          <Checkbox checked={tracked} onCheckedChange={(v) => setTracked(!!v)} className="mt-0.5" />
          <span>I tracked every dollar I spent for 7 days.</span>
        </label>
        <label className="flex items-start gap-3">
          <Checkbox checked={bills} onCheckedChange={(v) => setBills(!!v)} className="mt-0.5" />
          <span>I listed every income source and every bill.</span>
        </label>
        <label className="flex items-start gap-3">
          <Checkbox checked={opened} onCheckedChange={(v) => setOpened(!!v)} className="mt-0.5" />
          <span>I keep my savings separate from spending money (account or envelope).</span>
        </label>
        <label className="flex items-start gap-3">
          <Checkbox checked={auto} onCheckedChange={(v) => setAuto(!!v)} className="mt-0.5" />
          <span>I set money aside every payday.</span>
        </label>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Amount per transfer (optional)</p>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$50" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Your when-then plan</p>
          <Textarea rows={2} value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="When I get paid on Friday, then $50 moves to savings." />
        </div>
        <button
          disabled={save.isPending}
          onClick={() => save.mutate({
            savings_account_opened: opened, auto_transfer_set: auto, spending_tracked: tracked, bills_listed: bills,
            auto_transfer_amount: amount ? Number(amount.replace(/[^0-9.]/g, "")) || null : null,
            when_then_plan: plan.trim() || null,
          }, { onSuccess: onDone })}
          className={`${btn} bg-gold text-foreground w-full`}
        >Save</button>
      </div>
    </>
  );
}

export function CheckinForm({ onDone }: { onDone: () => void }) {
  const { stats } = useMoneyFoundation();
  const checkin = useMoneyCheckin();
  const l = stats.latest;
  const [f, setF] = useState({
    savings: l?.savings?.toString() ?? "", total_debt: l?.total_debt?.toString() ?? "", debt_paid: "",
    fico: "", monthly_income: l?.monthly_income?.toString() ?? "", monthly_debt_payments: l?.monthly_debt_payments?.toString() ?? "",
    monthly_expenses: l?.monthly_expenses?.toString() ?? "", monthly_housing: (l as any)?.monthly_housing?.toString() ?? "", gave: "",
  });
  const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(/[^0-9.]/g, "")));
  const field = (k: keyof typeof f, label: string, ph: string) => (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <Input inputMode="decimal" value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </div>
  );
  const ok = f.savings.trim() !== "" && f.total_debt.trim() !== "";
  return (
    <>
      <DialogHeader><DialogTitle>Weekly money check-in</DialogTitle></DialogHeader>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          {field("savings", "Savings balance", "$0")}
          {field("total_debt", "Total debt owed", "$0")}
          {field("debt_paid", "Debt paid this week", "$0")}
          {field("fico", "FICO (monthly, optional)", "700")}
          {field("gave", "True Fast giving this week", "$0")}
        </div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-semibold pt-2">Monthly numbers (update when they change)</p>
        <div className="grid grid-cols-3 gap-3">
          {field("monthly_income", "Income before tax", "$4,000")}
          {field("monthly_debt_payments", "Debt payments", "$600")}
          {field("monthly_expenses", "Total expenses", "$3,000")}
          {field("monthly_housing", "Mortgage / rent", "$1,500")}
        </div>
        <button
          disabled={!ok || checkin.isPending}
          onClick={() => checkin.mutate({
            savings: num(f.savings) ?? 0, total_debt: num(f.total_debt) ?? 0, debt_paid: num(f.debt_paid) ?? 0,
            fico: num(f.fico), monthly_income: num(f.monthly_income), monthly_debt_payments: num(f.monthly_debt_payments),
            monthly_expenses: num(f.monthly_expenses), monthly_housing: num(f.monthly_housing), gave: num(f.gave), wasMissedTwo: stats.missedTwo,
          }, { onSuccess: onDone })}
          className={`${btn} bg-gold text-foreground w-full disabled:opacity-40`}
        >{checkin.isPending ? "Saving…" : "Save check-in"}</button>
      </div>
    </>
  );
}

export function Lessons({ seatLessons, done }: { seatLessons: string[]; done: Set<string> }) {
  const complete = useCompleteMoneyLesson();
  const [open, setOpen] = useState<string | null>(null);
  const [pick, setPick] = useState<number | null>(null);
  const ordered = [...MONEY_LESSONS].sort((a, b) => Number(!seatLessons.includes(a.id)) - Number(!seatLessons.includes(b.id)));
  const lesson = MONEY_LESSONS.find((x) => x.id === open);

  if (lesson) {
    const right = pick === lesson.answer;
    return (
      <>
        <DialogHeader><DialogTitle>{lesson.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-foreground">
          {lesson.body.map((p, i) => <p key={i} className="leading-relaxed">{p}</p>)}
          <p className="font-semibold pt-2">{lesson.question}</p>
          {lesson.options.map((o, i) => (
            <button key={i} onClick={() => setPick(i)}
              className={`block w-full text-left rounded-md border px-3 py-2 ${pick === i ? (right ? "border-emerald-600 bg-emerald-50" : "border-destructive bg-destructive/10") : "border-border"}`}>
              {o}
            </button>
          ))}
          {pick !== null && !right && <p className="text-xs text-destructive">Not quite — reread and try again.</p>}
          <div className="flex gap-2">
            <button onClick={() => { setOpen(null); setPick(null); }} className={`${btn} border border-border`}>Back</button>
            <button disabled={!right || complete.isPending}
              onClick={() => complete.mutate(lesson.id, { onSuccess: () => { setOpen(null); setPick(null); } })}
              className={`${btn} bg-gold text-foreground flex-1 disabled:opacity-40`}>Mark complete</button>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <DialogHeader><DialogTitle>Money lessons</DialogTitle></DialogHeader>
      <ul className="space-y-2">
        {ordered.map((x) => (
          <li key={x.id}>
            <button onClick={() => setOpen(x.id)} className="w-full flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm text-left hover:border-gold">
              <span className="text-foreground">{x.title}{seatLessons.includes(x.id) && <span className="ml-2 text-[10px] uppercase tracking-wider text-gold-dark">Required now</span>}</span>
              {done.has(x.id) ? <Check className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
